import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useTranslations } from 'hooks/useTranslations';
import { Elabels } from '../helpers';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useNavigate } from 'react-router-dom';
import { FormInput } from 'components/molecules/FormInput';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { StyledSearchForm } from 'theme/styles';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { prepareFilters } from 'helpers/filters';
import {
  FormAsyncMultiAutocomplete,
  ILoadOptionsResult,
} from 'components/molecules/FormAsyncMultiAutocomplete';
import { useCase } from '../hooks';
import { EPerPages } from 'models/table';
import { IFormElementOptionProp } from 'models/form';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';

export const initialApplicationsSearchFormValues: ISearchFormValues = {
  search: '',
  legalName: '',
  assignedUsersUniqueIds: [],
  caseStatuses: [],
  processTypes: [],
  licenceAuthorities: [],
  regions: [],
};

export interface ISearchFormValues {
  search: string;
  legalName: string;
  assignedUsersUniqueIds: string[];
  caseStatuses: string[];
  processTypes: string[];
  licenceAuthorities: string[];
  regions: string[];
}

interface IApplicationsSearchForm {
  onSubmit: (arg: ISearchFormValues) => void;
  weaponLicencingAuthority: IDictionary[] | null;
  caseProcessTypes: IDictionary[] | null;
  weaponLicenceCaseStatus: IDictionary[] | null;
  region: IDictionary[] | null;
  initialFormValues?: ISearchFormValues;
}

const CaseSearchForm = ({
  onSubmit,
  weaponLicencingAuthority,
  caseProcessTypes,
  weaponLicenceCaseStatus,
  region,
  initialFormValues,
}: IApplicationsSearchForm) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const resolvedInitialValues = initialFormValues ?? initialApplicationsSearchFormValues;
  const hasActiveAdvancedFilters =
    !!initialFormValues &&
    (!!initialFormValues.legalName ||
      initialFormValues.assignedUsersUniqueIds.length > 0 ||
      initialFormValues.caseStatuses.length > 0 ||
      initialFormValues.processTypes.length > 0 ||
      initialFormValues.licenceAuthorities.length > 0 ||
      initialFormValues.regions.length > 0);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(hasActiveAdvancedFilters);
  const [values, setValues] = useState<ISearchFormValues>(resolvedInitialValues);
  const { getAssignedUsersCaseData, paginatorAssignedUsers } = useCase();
  const { isDesktop } = useThemeBreakpoint();

  const [assignedUsersCase, setAssignedUsersCase] = useState<IFormElementOptionProp[]>([]);

  const initialValuesAppliedRef = useRef(false);

  useEffect(() => {
    if (initialValuesAppliedRef.current) {
      return;
    }
    if (!initialFormValues) {
      initialValuesAppliedRef.current = true;
      return;
    }
    if (!weaponLicencingAuthority || !caseProcessTypes || !weaponLicenceCaseStatus) {
      return;
    }
    initialValuesAppliedRef.current = true;
    setValues(resolvedInitialValues);
    reset(resolvedInitialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues, weaponLicencingAuthority, caseProcessTypes, weaponLicenceCaseStatus]);

  useEffect(() => {
    getAssignedUsersCaseData();
  }, []);

  const loadOptions = async (page?: any, searchName?: string): Promise<ILoadOptionsResult> => {
    const response = await getAssignedUsersCaseData(page, EPerPages.perPage25, {
      name: searchName,
    });
    if (response) {
      const content = response.content?.map((item: any) => ({
        label: `${item.firstName} ${item.lastName} (${item.displayUniqueId})`,
        value: item.uniqueId,
      }));

      return {
        content: content,
        last: response.last,
      };
    }
    return { content: [], last: true };
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISearchFormValues>({
    defaultValues: resolvedInitialValues,
  });

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      if (
        [
          'assignedUsersUniqueIds',
          'caseStatuses',
          'processTypes',
          'licenceAuthorities',
          'regions',
        ].includes(name)
      ) {
        reset({ ...getValues(), [name]: [] });
      } else {
        reset({ ...getValues(), [name]: '' });
      }
    } else {
      reset(initialApplicationsSearchFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          container
          columnSpacing={2}
          sx={{
            flexWrap: { md: 'nowrap', xs: 'wrap' },
            alignItems: 'center',
            rowGap: 2,
            mb: 2,
          }}
        >
          <Grid
            container
            size={{ xs: 12 }}
            sx={{ justifyContent: 'flex-start', flexWrap: 'nowrap', gap: 0 }}
          >
            <FormInput
              charsLimit={120}
              maxLength
              InputProps={{
                style: {
                  height: '40px',
                  margin: 0,
                  width: isDesktop ? '330px' : 'auto',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root fieldset': {
                  borderEndEndRadius: 0,
                  borderStartEndRadius: 0,
                },
              }}
              withValidation={false}
              name="search"
              withLabel={false}
              placeholder={t(Elabels.caseIdOrName)}
              control={control}
              setValue={setValue}
              errors={errors}
              fullWidth={!isDesktop}
              id="case_search_form_search-input"
            />
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.medium}
              label={t('global.search.button')}
              type="submit"
              sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              id="case_search_form_search-button"
            />
            <Button
              variant={EButtonVariants.outlined}
              sizePX={EButtonSizesPX.px40}
              icon={EIconTypes.filters2}
              type="submit"
              sx={{ ml: '20px' }}
              onClick={() => setShowAdvanced(!showAdvanced)}
              id="case_search_form_filters2-button"
            />
          </Grid>
        </Grid>
        {showAdvanced && (
          <SearchWrapper>
            <StyledSearchForm
              container
              style={{ justifyContent: 'space-between' }}
              spacing={2}
            >
              <Grid size={{ xs: 12, md: 4 }}>
                <FormAsyncMultiAutocomplete
                  dictionaryType={EDictionaryTypes.ArtAccessoryCalibre}
                  loadOptions={loadOptions}
                  setSelected={setAssignedUsersCase}
                  keepSelectedInPlace
                  externalLastElements={paginatorAssignedUsers?.last}
                  name="assignedUsersUniqueIds"
                  label={t(Elabels.assignedUsersUniqueIds)}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormInput
                  name="legalName"
                  setValue={setValue}
                  label={t(Elabels.applicants)}
                  control={control}
                  errors={errors}
                  id="case_search_form_legal_name-input"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(weaponLicenceCaseStatus)}
                  name="caseStatuses"
                  label={t(Elabels.caseStatuses)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(
                    caseProcessTypes
                      ? caseProcessTypes?.filter(
                          (item) => item.uniqueId !== ECaseProcessTypes.OTHER_REQUEST,
                        )
                      : [],
                  )}
                  name="processTypes"
                  label={t(Elabels.processTypes)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                  name="licenceAuthorities"
                  label={t(Elabels.licenceAuthorities)}
                  canBeEmpty
                  searchable={false}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              {/*
                Regions filter temporarily hidden (per request).
              <Grid size={{ xs: 12, md: 4 }}>
                <FormMultiSelect
                  options={mapDictionaryToOptionProp(region)}
                  name="regions"
                  label={t(Elabels.regions)}
                  canBeEmpty
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </Grid>
              */}

              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  label={t('global.search.button')}
                  type="submit"
                  fullWidth
                  alignWithInputSX
                  id="case_search_form_search-button"
                />
              </Grid>
            </StyledSearchForm>
            <SelectedFilters
              name="case"
              values={values}
              excludes={['perPage', 'currentPage', 'search']}
              labels={Elabels}
              handleDelete={handleResetField}
              showSaveFilters={false}
              saveRouterStateAfterClearAll={false}
              dictionaries={{
                assignedUsersUniqueIds: prepareFilters(assignedUsersCase, 'value', 'label'),
                caseStatuses: prepareFilters(weaponLicenceCaseStatus, 'uniqueId', 'name'),
                processTypes: prepareFilters(caseProcessTypes, 'uniqueId', 'name'),
                licenceAuthorities: prepareFilters(
                  weaponLicencingAuthority,
                  'uniqueId',
                  'name',
                ),
                regions: prepareFilters(region, 'uniqueId', 'name'),
              }}
            />
          </SearchWrapper>
        )}
      </form>
    </div>
  );
};

export { CaseSearchForm };
